import React, { memo, useMemo } from "react";
import { Source, Layer } from "@vis.gl/react-maplibre";
import {
  CIRCLE_RADIUS,
  POLYGON_OPACITY,
  POLYGON_STROKE_WIDTH,
} from "@/constants/map";

const EMPTY_FEATURE_COLLECTION = {
  type: "FeatureCollection",
  features: [],
};

const closeRing = (coords) => {
  if (!coords || coords.length < 3) return null;

  const first = coords[0];
  const last = coords[coords.length - 1];

  if (first[0] !== last[0] || first[1] !== last[1]) {
    return [...coords, first];
  }

  return coords;
};

const buildPolygonGeometry = (value) => {
  if (!value) return null;

  // Обычный polygon: [{ lat, lon }, ...]
  if (Array.isArray(value)) {
    const coords = value.map(({ lat, lon }) => [lon, lat]);
    const ring = closeRing(coords);

    if (!ring) return null;

    return {
      type: "Polygon",
      coordinates: [ring],
    };
  }

  // MultiPolygon: { polygons: [[{ lat, lon }, ...], ...] }
  if (value.polygons?.length) {
    const multiCoords = value.polygons
      .map((polygon) => {
        if (!polygon?.length) return null;

        const coords = polygon.map(({ lat, lon }) => [lon, lat]);
        const ring = closeRing(coords);

        return ring ? [ring] : null;
      })
      .filter(Boolean);

    if (!multiCoords.length) return null;

    return {
      type: "MultiPolygon",
      coordinates: multiCoords,
    };
  }

  return null;
};

const buildGeoCollections = (objects = []) => {
  const points = [];
  const polygons = [];

  objects.forEach(({ definition, object }) => {
    definition.fields.forEach((field) => {
      const value = object[field.field_name];

      if (field.type === "point") {
        const point = value ? Object.values(value) : null;

        if (!point || point.length < 2) return;

        points.push({
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [point[1], point[0]],
          },
          properties: {
            uuid: object.uuid,
            definition_uuid: definition.uuid,
            field_name: field.field_name,
            field_type: field.type,
            color: definition.color,
          },
        });

        return;
      }

      if (field.type === "polygon" || field.type === "parent_polygon") {
        const geometry = buildPolygonGeometry(value);

        if (!geometry) return;

        polygons.push({
          type: "Feature",
          geometry,
          properties: {
            uuid: object.uuid,
            definition_uuid: definition.uuid,
            field_name: field.field_name,
            field_type: field.type,
            color: definition.color,
          },
        });
      }
    });
  });

  return {
    points: {
      type: "FeatureCollection",
      features: points,
    },
    polygons: {
      type: "FeatureCollection",
      features: polygons,
    },
  };
};

const GeoLayerGroup = ({
  idPrefix,
  points,
  polygons,
  beforeId,
  isSatellite,
}) => {
  return (
    <>
      <Source id={`${idPrefix}-polygons-source`} type="geojson" data={polygons}>
        {isSatellite && (
          <Layer
            id={`${idPrefix}-polygons-outline-bg`}
            beforeId={beforeId}
            type="line"
            paint={{
              "line-color": "#ffffff",
              "line-width": POLYGON_STROKE_WIDTH + 1,
              "line-opacity": 0.9,
            }}
          />
        )}

        {isSatellite && (
          <Layer
            id={`${idPrefix}-polygons-fill-bg`}
            beforeId={beforeId}
            type="fill"
            paint={{
              "fill-color": "#ffffff",
              "fill-opacity": POLYGON_OPACITY,
            }}
          />
        )}

        <Layer
          id={`${idPrefix}-polygons-fill`}
          beforeId={beforeId}
          type="fill"
          paint={{
            "fill-color": ["get", "color"],
            "fill-opacity": POLYGON_OPACITY,
          }}
        />

        <Layer
          id={`${idPrefix}-polygons-outline`}
          beforeId={beforeId}
          type="line"
          paint={{
            "line-color": ["get", "color"],
            "line-width": POLYGON_STROKE_WIDTH,
            "line-opacity": 1,
          }}
        />
      </Source>

      <Source id={`${idPrefix}-points-source`} type="geojson" data={points}>
        {isSatellite && (
          <Layer
            id={`${idPrefix}-points-bg`}
            beforeId={beforeId}
            type="circle"
            paint={{
              "circle-radius": CIRCLE_RADIUS,
              "circle-color": "#ffffff",
              "circle-opacity": POLYGON_OPACITY,
              "circle-stroke-opacity": 1,
              "circle-stroke-color": "#ffffff",
              "circle-stroke-width": POLYGON_STROKE_WIDTH + 1,
            }}
          />
        )}

        <Layer
          id={`${idPrefix}-points`}
          beforeId={beforeId}
          type="circle"
          paint={{
            "circle-radius": CIRCLE_RADIUS,
            "circle-color": ["get", "color"],
            "circle-opacity": POLYGON_OPACITY,
            "circle-stroke-opacity": 1,
            "circle-stroke-color": ["get", "color"],
            "circle-stroke-width": POLYGON_STROKE_WIDTH,
          }}
        />
      </Source>
    </>
  );
};

export const GeoLayers = ({ objects = [], mapStyle }) => {
  const isSatellite = mapStyle === "satellite";

  const collections = useMemo(() => {
    if (!objects.length) {
      return {
        points: EMPTY_FEATURE_COLLECTION,
        polygons: EMPTY_FEATURE_COLLECTION,
      };
    }

    return buildGeoCollections(objects);
  }, [objects]);

  return (
    <GeoLayerGroup
      idPrefix="geo"
      points={collections.points}
      polygons={collections.polygons}
      beforeId="highlighted-anchor"
      isSatellite={isSatellite}
    />
  );
};

export default memo(GeoLayers);
